import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, Button, Typography, COLORS, Modal, ModalHeader, ModalContent, ModalFooter, ModalButtons, Radio, RadioGroup } from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import { Notify } from 'components/Notifications/Notifications';
import { extractErrorMessage } from 'utils/errorUtils';
import { decodeHtmlEntities } from 'utils/htmlEntities';
import { encodeQueryValue } from 'utils/urlEncoding';
import ModalRouteBackground from 'components/virtual-library/ModalRouteBackground';
import {
  useCategoryDeleteInitQuery,
  useDeleteCategoryMutation,
} from 'api/useLibraryApi';
import { useModalClose } from 'hooks';
import { ROUTES } from 'constants/constants';
import type { CategorySelectorItem } from 'types';

/** Router state passed from Sidebar when clicking Delete on a category. */
interface DeleteCategoryState {
  categoryName: string;
  /** Parent lineage, e.g. "/Home/" — the folder containing this category. */
  lineage: string;
}

const DeleteCategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as DeleteCategoryState | null;

  const categoryName = state?.categoryName ?? '';
  const lineage = state?.lineage ?? '/Home/';
  const displayCategoryName = decodeHtmlEntities(categoryName);

  // ── API queries ────────────────────────────────────────────────────
  const {
    data: selectorItems,
    isLoading: initLoading,
    isError: initError,
    error: initErrorObj,
  } = useCategoryDeleteInitQuery(lineage, categoryName);

  // If init data fails to load, show a toast and navigate back to library
  useEffect(() => {
    if (initError) {
      Notify.error(extractErrorMessage(initErrorObj, 'Failed to load folder data. Please try again.'));
      navigate(ROUTES.library);
    }
  }, [initError, initErrorObj, navigate]);

  // ── Which-location radio state ──────────────────────────────────────
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    if (!selectorItems || selectorItems.length === 0) return;
    const firstDeletableIndex = selectorItems.findIndex((item) => item.childCount === 0);
    if (firstDeletableIndex >= 0) {
      setSelectedIndex(firstDeletableIndex);
    } else {
      setSelectedIndex(0);
    }
  }, [selectorItems]);

  // Derive selected item and its properties
  const selectedItem: CategorySelectorItem | undefined = selectorItems?.[selectedIndex];
  const hasChildren = selectedItem ? selectedItem.childCount > 0 : false;
  const hasAnyDeletableLocation =
    selectorItems?.some((item) => item.childCount === 0) ?? false;

  // ── Confirmation dialog ──────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);


  // ── Delete mutation ────────────────────────────────────────────────
  const [deleteCategory, { isLoading: deleting }] = useDeleteCategoryMutation();

  const handleViewChildItems = () => {
    // Keep original category token for routing/query semantics; sanitize only for display.
    const folderLineage = `${lineage}${categoryName}/`;
    navigate(`${ROUTES.browse}?lineage=${encodeQueryValue(folderLineage)}`);
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await deleteCategory(String(selectedItem.id)).unwrap();
      Notify.success(`Deleted folder "${categoryName}".`);
      navigate(-1);
    } catch (e) {
      Notify.error(extractErrorMessage(e, 'Failed to delete folder. Please try again.'));
    }
  };

  const handleClose = useModalClose();

  // Guard: if navigated to directly without state, bounce back.
  if (!categoryName) {
    return (
      <>
      <ModalRouteBackground />
      <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="delete-folder-title" PaperProps={{ sx: { width: '640px', maxWidth: '640px' } }}>
        <ModalHeader id="delete-folder-title" title="Delete folder" onClose={handleClose} />
        <ModalContent>
          <Typography variant="p-md" style={{ color: COLORS.red[800] }}>
            No folder selected. Please return to the library and try again.
          </Typography>
        </ModalContent>
      </Modal>
      </>
    );
  }

  // Build readable breadcrumb: "Home > CategoryName"
  const breadcrumbParts = lineage
    .split('/')
    .filter(Boolean)
    .slice(1)
    .map((part) => decodeHtmlEntities(part)); // skip leading 'Home' if lineage starts with /Home/
  const breadcrumbText = ['Home', ...breadcrumbParts, displayCategoryName].join(' > ');

  return (
    <>
    <ModalRouteBackground />
    <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="delete-folder-title" PaperProps={{ sx: { width: '640px', maxWidth: '640px' } }}>
      <ModalHeader id="delete-folder-title" title="Delete folder" onClose={handleClose} />

      <ModalContent>
      {/* Warning when selected location has children */}
      {hasChildren && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This folder cannot be deleted at the selected location because it contains child folders or resources.
          {' '}Delete child items first.
          <Box sx={{ mt: 1 }}>
            <Button size="small" color="secondary" onClick={handleViewChildItems}>
              View child items
            </Button>
          </Box>
        </Alert>
      )}

      <Stack gap={2}>
        {/* Current category breadcrumb */}
        <Box>
          <Typography variant="p-sm" component="span" style={{ fontWeight: 700, color: COLORS.gray[700] }}>
            Current Folder:{' '}
          </Typography>
          <Typography variant="p-md" component="span" style={{ color: COLORS.gray[800] }}>
            {breadcrumbText}
          </Typography>
        </Box>

        {/* Which Location? radio group */}
        <Box>
          <Typography variant="p-sm" style={{ fontWeight: 700, color: COLORS.gray[700], display: 'block', marginBottom: 6 }}>
            Which Location?
          </Typography>
          {initLoading ? (
            <Typography variant="p-sm" style={{ color: COLORS.gray[400], fontStyle: 'italic' }}>Loading locations…</Typography>
          ) : initError ? (
            <Typography variant="p-sm" style={{ color: COLORS.red[800] }}>Failed to load location data.</Typography>
          ) : !selectorItems || selectorItems.length === 0 ? (
            <Typography variant="p-sm" style={{ color: COLORS.gray[400], fontStyle: 'italic' }}>No location data available.</Typography>
          ) : (
            <Box
              sx={{
                maxHeight: 220,
                overflowY: 'auto',
                border: `1px solid ${COLORS.gray[200]}`,
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <RadioGroup
                id="delete-category-location"
                aria-label="Which location?"
                name="delete-category-location"
                value={String(selectedIndex)}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, val: string) => setSelectedIndex(Number(val))}
              >
                {selectorItems.map((item, idx) => (
                  <Radio
                    key={item.id}
                    id={`delete-cat-loc-${idx}`}
                    name="delete-category-location"
                    value={String(idx)}
                    label={decodeHtmlEntities(item.locationName)}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}

          {!initLoading && !initError && !hasAnyDeletableLocation && (
            <Typography variant="p-sm" style={{ color: COLORS.gray[700], marginTop: 8 }}>
              No deletable locations are currently available for this folder.
            </Typography>
          )}
        </Box>

      </Stack>
      </ModalContent>

      <ModalFooter>
        <ModalButtons>
          <Button
            variant="contained"
            color="error"
            disabled={hasChildren || deleting || initLoading || !selectedItem}
            onClick={() => setConfirmOpen(true)}
          >
            Delete
          </Button>
          <Button
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </ModalButtons>
      </ModalFooter>
    </Modal>

      {/* Delete confirmation dialog — childCount is 0 */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} ariaLabelledby="delete-cat-confirm-title">
        <ModalHeader title="Confirm Delete" onClose={() => setConfirmOpen(false)} />
        <ModalContent>
          <Typography variant="p-md">
            Are you sure you want to delete <strong>{displayCategoryName}</strong>?
            This cannot be undone.
          </Typography>
        </ModalContent>
        <ModalFooter>
          <ModalButtons>
            <Button color="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={deleting}
              onClick={async () => {
                setConfirmOpen(false);
                await handleDelete();
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </ModalButtons>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DeleteCategoryPage;
